/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getDiscountTotal,
    getOrderPromotions,
    getPromotionsDataView,
    getShippingPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CartTotalDisplay, { CartTotalDisplayStyles } from "@insite/content-library/Components/CartTotalDisplay";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const promotions = getPromotionsDataView(state, state.pages.requisitionConfirmation.cartId).value;

    return {
        cart: getCartState(state, state.pages.requisitionConfirmation.cartId).value,
        orderPromotions: promotions ? getOrderPromotions(promotions) : undefined,
        shippingPromotions: promotions ? getShippingPromotions(promotions) : undefined,
        discountTotal: promotions ? getDiscountTotal(promotions) : undefined,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface XStyle {
    wrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    itemsCount?: TypographyPresentationProps;
}

export interface RequisitionConfirmationTotalStyles {
    cartTotal?: CartTotalDisplayStyles;

    // custom
    xRequisitionConfirmationTotalStyles?: XStyle;
}

export const requisitionConfirmationTotalStyles: RequisitionConfirmationTotalStyles = {};

const styles = requisitionConfirmationTotalStyles;

const RequisitionConfirmationTotal = ({ cart, orderPromotions, shippingPromotions, discountTotal }: Props) => {
    // xng-change: adding wrappers for total section
    return (
        <>
            <Typography {...styles.xRequisitionConfirmationTotalStyles?.title}>{translate("Order Summary")}</Typography>
            <StyledWrapper {...styles.xRequisitionConfirmationTotalStyles?.wrapper}>
                <Typography {...styles.xRequisitionConfirmationTotalStyles?.itemsCount}>
                    {cart?.cartLines?.length || 0} {translate("Items")}
                </Typography>
                <CartTotalDisplay
                    cart={cart}
                    orderPromotions={orderPromotions}
                    shippingPromotions={shippingPromotions}
                    discountTotal={discountTotal}
                    extendedStyles={styles.cartTotal}
                />
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(RequisitionConfirmationTotal),
    definition: {
        displayName: "Total",
        allowedContexts: ["RequisitionConfirmationPage"],
        group: "Requisition Confirmation",
    },
};

export default widgetModule;
